import { useLocation, Link } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useEffect, useRef, useState } from 'react'
import './Sidebar.css'
import { IoHomeOutline, IoAddCircleOutline } from 'react-icons/io5'
import { LuComponent, LuLayers, LuTicket, LuUserCog } from 'react-icons/lu'
import { PiCertificate } from 'react-icons/pi'
import { CiViewTable } from 'react-icons/ci'
import { FiAlignJustify } from 'react-icons/fi'
import React from 'react'
import { FaBullseye, FaLink, FaSitemap, FaExternalLinkAlt, FaFileAlt } from 'react-icons/fa'
import { getV2Token } from '../../utils/v2AuthUtils'
import { isDemoUser } from '../../utils/demoAuth'
import LinksGroup from './LinksGroup/LinksGroup'

interface SidebarProps {
  isOpen: boolean
  onToggle: () => void
  language: 'en' | 'ar'
}

interface SidebarLink {
  page: string
  labelKey: string
  href: string
  icon: (props: any) => React.ReactElement
  external?: boolean
  externalUrl?: string | null
}

// Partner-only sidebar (when logged in as demo partner)
const partnerLinks: SidebarLink[] = [
  { page: 'dashboard', labelKey: 'Sidebar.Home', href: '/', icon: (props: any) => <IoHomeOutline {...props} /> },
  { page: 'questionnaires', labelKey: 'surveys.Surveys', href: '/demo-partner/questionnaires', icon: (props: any) => <PiCertificate {...props} /> },
  { page: 'documents', labelKey: 'Sidebar.DocumentManagement', href: '/demo-partner/documents', icon: (props: any) => <FaFileAlt {...props} /> },
  { page: 'compliance-status', labelKey: 'Sidebar.ComplianceStatus', href: '/demo-partner/status', icon: (props: any) => <LuComponent {...props} /> },
  { page: 'tickets', labelKey: 'Sidebar.Tickets', href: '/demo-partner/tickets', icon: (props: any) => <LuTicket {...props} /> },
  { page: 'certifications', labelKey: 'Sidebar.Certifications', href: '/demo-partner/certifications', icon: (props: any) => <PiCertificate {...props} /> },
  { page: 'assessment-requests', labelKey: 'Sidebar.PartnerAssessmentRequests', href: '/demo-partner/assessment-requests', icon: (props: any) => <CiViewTable {...props} /> },
  { page: 'profile', labelKey: 'Sidebar.PartnerProfile', href: '/demo-partner/profile', icon: (props: any) => <LuUserCog {...props} /> },
  { page: 'visibility', labelKey: 'Sidebar.PartnerVisibility', href: '/demo-partner/visibility', icon: (props: any) => <FaLink {...props} /> },
]

// Update links array to include icon components as functions
const links: SidebarLink[] = [
  { 
    page: 'dashboard', 
    labelKey: 'Sidebar.Home', 
    href: '/', 
    icon: (props: any) => <IoHomeOutline {...props} /> 
  },
  { 
    page: 'demo-partners', 
    labelKey: 'Sidebar.DemoPartners', 
    href: '/demo-partners', 
    icon: (props: any) => <CiViewTable {...props} />
  },
  { 
    page: 'standards', 
    labelKey: 'Sidebar.Reference', 
    href: '/standards', 
    icon: (props: any) => <CiViewTable {...props} />
  },
   { 
    page: 'Objectives', 
    labelKey: 'Sidebar.Objectives', 
    href: '/objective', 
    icon: (props: any) => <FaBullseye {...props} />
  },
  // { 
  //   page: 'frameWork', 
  //   labelKey: 'Sidebar.standard mapping', 
  //   href: '/frameWork', 
  //   icon: (props: any) => <FaLink {...props} />
  // },
  { 
    page: 'compliances', 
    labelKey: 'Sidebar.Compliance', 
    href: '/compliances', 
    icon: (props: any) => <LuComponent {...props} />
  },
  { 
    page: 'tickets', 
    labelKey: 'Sidebar.Tickets', 
    href: '/ticket', 
    icon: (props: any) => <LuTicket {...props} />
  },
  { 
    page: 'users', 
    labelKey: 'Sidebar.UserManagement', 
    href: '/user', 
    icon: (props: any) => <LuUserCog {...props} />
  },
  { 
    page: 'certifications', 
    labelKey: 'Sidebar.Certifications', 
    href: '/certifications', 
    icon: (props: any) => <PiCertificate {...props} />
  },
  { 
  page: 'onboxMessage', 
  labelKey: 'Sidebar.OneBox', 
  href: '/oneboxMessage', 
  icon: (props: any) => <LuLayers {...props} /> 
},

 { 
    page: 'organization-chart', 
    labelKey: 'Sidebar.OrganizationChart', 
    href: '/organization-chart', 
    icon: (props: any) => <FaSitemap {...props} />
  },

  { 
    page: 'announcements', 
    labelKey: 'Sidebar.Announcements', 
    href: '/announcements', 
    icon: (props: any) => <LuTicket {...props} />
  },
   { 
    page: 'QuestionControlTree', 
    labelKey: 'QuestionControlTree', 
    href: '/QuestionControlTree', 
    icon: (props: any) => <PiCertificate {...props} />
  },
  { 
    page: 'documents', 
    labelKey: 'Sidebar.DocumentManagement', 
    href: '/documents', 
    icon: (props: any) => <FaFileAlt {...props} />
  },
  { 
    page: 'assets', 
    labelKey: 'Sidebar.AssetsManagement', 
    href: '/assets', 
    icon: (props: any) => <LuLayers {...props} />
  },
   { 
    page: 'function', 
    labelKey: 'function.Functions', 
    href: '/function', 
    icon: (props: any) => <PiCertificate {...props} />
  },
  { 
    page: 'Invitation Team Onboarding', 
    labelKey: 'surveys.User Invitation', 
    href: '/team-onboarding', 
    icon: (props: any) => <PiCertificate {...props} />
  },
  { 
    page: 'enterprise-risk-management', 
    labelKey: 'Sidebar.EnterpriseRiskManagement', 
    href: 'https://demo.pianat.ai/', 
    icon: (props: any) => <FaExternalLinkAlt {...props} />,
    external: true,
    externalUrl: 'https://demo.pianat.ai/'
  },
];

const Sidebar: React.FC<SidebarProps> = ({ isOpen, onToggle, language }) => {
  const location = useLocation()
  const { t } = useTranslation()
  const sidebarRef = useRef<HTMLDivElement>(null)
  const isRTL = language === 'ar'
  const [isMobileOpen, setMobileOpen] = useState(false)
  const isPartner = isDemoUser(getV2Token())
  const menuLinks = isPartner ? partnerLinks : links



 
   

  useEffect(() => {
    if (isOpen && sidebarRef.current) {
      const activeItem = sidebarRef.current.querySelector('[data-active="true"]')
      activeItem?.scrollIntoView({ behavior: 'smooth', block: 'center' })
    }
  }, [location.pathname, isOpen])

  return (
    <div
      className={`d-none d-md-flex flex-column h-100 position-relative ${
        isOpen ? 'sidebar-expanded' : 'sidebar-collapsed pt-4'
      } ${isRTL ? 'rtl' : 'ltr'}`}
     
    >
      <div className="d-flex justify-content-center align-items-center sidebar-logo-wrap">
        <Link
          to="/"
          className="d-block position-relative"
          style={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}
        >
          {isOpen ? (
            <img
              src={process.env.PUBLIC_URL + '/assets/images/logoNew.png'}
              alt="logo"
              className="logocomply"
            />
          ) : (
            <img
              src={process.env.PUBLIC_URL + '/assets/images/logoNew.png'}
              alt="logo"
              style={{ width: '52px', height: '52px', objectFit: 'contain' }}
            />
          )}
        </Link>
      </div>

      <div ref={sidebarRef} className="overflow-auto" style={{ marginTop: 0 }}>
        <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
          {menuLinks.map((link) => {
            const active = location.pathname === link.href
            
            // Handle external links
            if (link.external && link.externalUrl) {
              return (
                <li key={link.href}>
                  <a
                    href={link.externalUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={`sidebar-link ${!isOpen ? 'justify-content-center' : ''}`}
                    title={!isOpen ? String(t(link.labelKey)) : ''}
                  >
                    {React.createElement(link.icon, {
                      className: "sidebar-icon mx-2",
                      style: { fontSize: !isOpen ? '2rem' : '1.5rem' }
                    })}
                    {isOpen && <span className="text-truncate">{t(link.labelKey)}</span>}
                  </a>
                </li>
              )
            }
            
            return (
              <li key={link.href}>
                <Link
                  to={link.href}
                  className={`sidebar-link ${active ? 'active' : ''} ${!isOpen ? 'justify-content-center' : ''}`}
                  data-active={active}
                  title={!isOpen ? String(t(link.labelKey)) : ''} 
                >
                  {React.createElement(link.icon, {
                    className: "sidebar-icon mx-2",
                    style: { fontSize: !isOpen ? '2rem' : '1.5rem' }
                  })}
                  {isOpen && <span className="text-truncate">{t(link.labelKey)}</span>}
                </Link>
              </li>
            )
          })}
          
          {/* Survey LinksGroup */}
          {!isPartner && (
            <LinksGroup
              header={t("Sidebar.Survey")}
              iconComponent={(props: any) => <PiCertificate {...props} />}
              childrenLinks={[
                {
                  name: t("Sidebar.Surveys"),
                  link: "/surveys",
                  icon: (props: any) => <CiViewTable {...props} />
                },
                {
                  name: t("Sidebar.CreateSurvey"),
                  link: "/create-survey",
                  icon: (props: any) => <IoAddCircleOutline {...props} />
                },
                {
                  name: t("Sidebar.Bank Questions"),
                  link: "/bankQuestions",
                  icon: (props: any) => <LuLayers {...props} />
                },
              ]}
              showIconOnly={!isOpen}
            />
          )}
        </ul>
      </div>

      {isMobileOpen && (
        <div 
          className="position-fixed top-0 start-0 w-75 h-100 sidebar-mobile shadow-lg zindex-modal d-md-none"
          style={{ zIndex: 1050 }}
        >
          <div className="d-flex justify-content-end p-3">
            <button className="btn btn-sm btn-light" onClick={() => setMobileOpen(false)}>
              ✕
            </button>
          </div>
          <div className="px-3">
            {menuLinks.map((link) => {
              const active = location.pathname === link.href
              
              // Handle external links in mobile menu
              if (link.external && link.externalUrl) {
                return (
                  <a
                    key={link.href}
                    href={link.externalUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="d-block py-2 text-decoration-none"
                    onClick={() => setMobileOpen(false)}
                  >
                    {React.createElement(link.icon, { className: "me-2" })} {t(link.labelKey)}
                  </a>
                )
              }
              
              return (
                <Link
                  key={link.href}
                  to={link.href}
                  className={`d-block py-2 text-decoration-none ${active ? 'fw-bold text-primary' : ''}`}
                  onClick={() => setMobileOpen(false)}
                >
                  {React.createElement(link.icon, { className: "me-2" })} {t(link.labelKey)}
                </Link>
              )
            })}
          </div>
        </div>
      )}
    </div>
  )
}

export default Sidebar